// Shared strings used across forms
export default class Strings {
    static SELECT_AGE = "Select Age *";
    static AGE = "Age *";
    static YOUR_PETS_NAME = "Your Pet's Name *";
    static PETS_NAME = "Pet's Name *";
    static NAME = "Name *";
    static NO_PET_NAME = "I don’t have my pet yet";
    static TEMP_PET_NAME = "Your Pet";
    static TELL_US_PET = "Tell Us About Your Pet";
    static ADD_A_PET = "Add a Pet";
    static ADD_PET = "Add Pet";
    static FIRST_NAME = "First Name *";
    static LAST_NAME = "Last Name *";
    static CAT_BREED_ORDER = [
        "Domestic Shorthair",
        "Domestic Shorthair Mix",
        "Domestic Mediumhair",
        "Domestic Medium Hair",
        "Domestic Medium Hair Mix",
        "Domestic Longhair",
        "Domestic Longhair Mix",
        "Mixed Cat"
    ];
    static ADD_ADDITIONAL_PET = "Add another pet";
    /** Multi-pet discount callouts, worded from the advertised rate. Where the wording differs by rule, `_EACH` is for a discount that includes the first pet. */
    static MULTIPET = {
        ADD_PET_CTA: (rate: number) => `Add a Pet, Get ${rate}% Off`,
        ADD_PET_CTA_EACH: (rate: number) => `Add a Pet, Get ${rate}% Off Each`,
        /** Same wording for both rules: members already have a pet enrolled, so every pet they add is discounted. */
        ADD_MORE_PETS_CTA: (rate: number) => `Add More Pets, Get ${rate}% Off Each`,
        MODAL_BADGE: (rate: number) => `${rate}% off all additional pets`,
        MODAL_BADGE_EACH: (rate: number) => `${rate}% off each pet`,
        STICKY_CTA: (rate: number) => `${Strings.ADD_A_PET} (${rate}% Off)`,
        STICKY_CTA_EACH: (rate: number) => `${Strings.ADD_A_PET} (${rate}% Off Each)`,
        DROPDOWN_BADGE: (rate: number) => `${rate}% multi-pet discount`,
        DROPDOWN_BADGE_EACH: (rate: number) => `${rate}% multi-pet discount on each pet`,
        HYBRID_BADGE: (rate: number) => `${rate}% multi-pet discount on all additional pets`,
        HYBRID_BADGE_EACH: (rate: number) => `${rate}% multi-pet discount on each pet`,
        OFF_BADGE: (rate: number) => `${rate}% Off`,
        OFF_BADGE_EACH: (rate: number) => `${rate}% Off Each`,
        SAVE_BADGE: (rate: number) => `Save ${rate}%`,
        /** Price summary once the discount is on the quote; takes the rated adjustment's percent, not the advertised rate. */
        DISCOUNT_APPLIED: (rate: number) => `${rate}% Multi-pet Discount Applied`,
        /** No advertised rate (lookup pending or failed, no zip): true in every state. */
        GENERIC: {
            ADD_PET_CTA: "Add a Pet & Save",
            ADD_MORE_PETS_CTA: "Add More Pets & Save",
            MODAL_BADGE: "Multi-pet discount",
            STICKY_CTA: "Add a Pet & Save",
            DROPDOWN_BADGE: "Multi-pet discount",
            HYBRID_BADGE: "Multi-pet discount available",
            OFF_BADGE: "Save",
            SAVE_BADGE: "Multi-pet savings"
        }
    };
    static ADD_PET_GET_DISCOUNT = Strings.MULTIPET.ADD_PET_CTA(10);
    static EDIT_PLAN = "Edit Plan";
    static LOCK_IN_SAVINGS = "Lock in savings — add all your pets today.";
    static MULTIPET_DISCOUNT_ADDITIONAL_PETS = Strings.MULTIPET.MODAL_BADGE(10);
    static MULTIPET_GENERIC_DISCLAIMER = "Up to 10% multi-pet discount available. Discounts vary by state and are subject to change. Not available in all states.";
    static DISCOUNTS_APPLIED = "Discount(s) Applied";
    static DISCOUNT_SAVINGS = "Discount Savings:";
    static ENTER_PET_INFO = "Enter Pet Info";
    static PET_STEP_LABEL = "Pet Info";
    static MORE_PETS = "Got more pets? Add them on the next screen.";
    static SELECT_MONTH = " Birth Month *";
    static SELECT_YEAR = " Birth Year *";
    static SELECT_BREED = "Select Breed *";
    static BREED = "Breed *";
    /** Breed label carrying the pet's name, for surfaces that show more than one pet's breed. */
    static BREED_FOR = (petName: string) => `Breed for ${petName} *`;
    static SEARCH_BREEDS = "Search Breeds";
    static YOUR_FIRST_NAME = "Your First Name *";
    static YOUR_LAST_NAME = "Your Last Name *";
    static EMAIL_ADDRESS = "Email Address *";
    static GET_SAVED_QUOTE = "Get saved quote";
    static ZIP_CODE = "Zip Code *";
    // ENG-16887 zip autocomplete — focus-state placeholder only; the label stays ZIP_CODE.
    static ZIP_WHERE_PET_LIVES = "Zip where your pet lives";
    static ZIP_SUGGESTIONS_LABEL = "Zip code suggestions";
    static ZIP_NOT_FOUND = "We couldn't locate that Zip Code";
    static CALCULATING = "Calculating...";
    static ANNUAL_BILLING_LABEL = "Annual billing";
    static MULTIPET_LINKED_LABEL = "Apply to all pets";
    static MULTIPET_TOGGLE_HINT = "Click or tap names to toggle between pets";
    static NOT_APPLICABLE = "N/A";
    static THANKS_JOINING = "Thanks for joining the pack!";
    static PROBLEM_FETCH_QUOTE = `We’re having trouble fetching your quote.`;
    static CLOSE_NOTIFICATION = "Close notification";
    static CREATE_PLAN = "Create your plan";
    static PREVENTIVE_CARE_HEADER = "Add preventive care coverage";

    static PREVENTIVE_CARE_SUBHEADER = "Get fixed reimbursements for wellness care every year.";
    static PROCEED_TO_CHECKOUT = "Proceed to Checkout";
    static SELECT_PLAN = "Select Plan";
    static QUICK_CHECKOUT = "Quick Checkout";
    static MOBILE_PHONE = "Mobile Number";
    static MOBILE_PHONE_OPTIONAL = "Mobile Number (optional)";
    static UNLIMITED_ANNUAL_LIMIT_LABEL = "Unlimited";
    static EDIT_PETS = "Edit Pets";
    static EDIT_PET = "Edit Pet";
    static EDIT = "Edit";
    static EDIT_PET_DETAILS = "Edit Pet Details";
    static REMOVE_PET = "Remove Pet";
    static REMOVE_PET_CONFIRMATION = "Are you sure you want to remove";
    static CONFIRMATION_CANCEL = "No, cancel";
    static COMPLETE_CHECKOUT = "Complete Checkout";
    static COVERAGE_DETAILS = "Coverage Details";
    static TERMS_MEANING = "What do these terms mean?";
    static WRAPPING_UP = "Wrapping up...";
    static START_OVER = "Start Over";
    static ADD_MULTIPLE_PETS_LATER = "You can add multiple pets later";
    static ADD_EDIT_PETS_LATER = "You can add/edit pets later";
    static OWNER_FORM_HEADER = "Tell Us About You";
    static OWNER_FORM_SUBHEADER = "We need this info to generate a quote";
    static SAVE = "Save";
    static CLOSE = "Close";
    static ANNUAL_LIMIT = "Annual Limit";
    static REIMBURSEMENT_RATE = "Reimbursement Rate";
    static ANNUAL_DEDUCTIBLE = "Annual Deductible";
    static CUSTOMIZE = "Customize";
    static CUSTOMIZE_PLAN = "Customize Plan";
    static AND_SO_MUCH_MORE = "And so much more";
    static UNSAVED_CHANGES_TITLE = "You have unsaved changes";
    static UNSAVED_CHANGES_MESSAGE = "Would you like to discard your changes or continue editing?";
    static UNSAVED_CHANGES_DISCARD = "Discard";
    static UNSAVED_CHANGES_STAY = "Keep Editing";
    static POLICY_NEXT_STEP_CTA = "Check Pricing";
    static CALL_US = "Call Us";
    static CREDIT_DEBIT_CARD = "Credit/Debit Card";
    static PAYMENT_INFO = "Payment Info";
    static PAYMENT_CHOICE = "Or pay with a card";
    static PREVENTIVE_HEADER = "Add Preventive Care Coverage";
    static MUCH_MORE = "and much more";
    static COVERAGE_MODAL_CTA = "Full Coverage Details";
    static EXCLUSIONS_MODAL_CTA = "View All Exclusions";
    static COVERED_CTA = "What's Covered";
    static NOT_COVERED_CTA = "What's not Covered";
    static COVERAGE_ACCIDENT_ILLNESS_HEADER = "Spot plans cover eligible diagnostics and treatments for costly accidents and illnesses like:";
    static COVERAGE_ACCIDENT_ONLY_HEADER = "Spot plans cover eligible diagnostics and treatments for costly accidents like:";
    static COVERAGE_EXCLUSIONS_HEADER = "Like all pet insurance providers, we have exclusions.";
    static SAMPLE_POLICY_CTA = "View a Sample Policy";
    static ELIGIBLE_CLAIMS_HEADER = "Helps cover eligible claims for:";
    static HIGHLIGHTED_PLAN_HEADER = "Spot plans help cover your pet from head-to-tail.";
    static FORBES_ANNUAL_LIMIT = "“Best for annual coverage choices”";
    static QUOTE_UPDATING = "Updating your quote...";
    static PROCESSING = "Processing...";
    static REDIRECTING = "Redirecting...";
    static QUOTE_UPDATE_SUCCESS = "Quote updated successfully";
    static ANNUAL_LIMIT_MEANING = "The total amount you can be reimbursed over one 12-month policy period. This limit resets whenever a new policy period begins.";
    static REIMBURSEMENT_MEANING = "After the deductible is met, we will reimburse a percentage of covered expenses according to the reimbursement percentage you select.";
    static ANNUAL_DEDUCTIBLE_MEANING =
        "The amount you must satisfy for covered veterinary expenses before you can start being reimbursed. You’ll only need to satisfy it once per 12-month policy period.";
    static ANNUAL_LIMIT_HINT = "Max amount you can be reimbursed per year";
    static REIMBURSEMENT_RATE_HINT = "How much cash back you get for eligible claims";
    static ANNUAL_DEDUCTIBLE_HINT = "What you pay before reimbursement starts";
    static EXISTING_POLICY_HEADER = "Welcome back! You have a Spot policy already.";
    static ENROLL_MULTIPET_DISCOUNT = "Enroll a new pet to save with multi-pet discount";
    static START_QUOTE_DIFFERENT_EMAIL = "I want to start a quote with a different email";
    static MANAGE_POLICY = "Manage my policy";
    static MANAGE_POLICY_DESCRIPTION = "View coverage, billing, or make changes";

    static MORE_AFFORDABLE = "More Affordable";
    static MOST_POPULAR = "Most Popular";
    static MOST_CASHBACK = "Most Cash back";
    static MORE_CASHBACK = "More Cash back";
    static QUICK_ENROLL_HEADER = "Quick Enroll";

    static ERRORS = {
        PET_NAME_CHARS: "Pet name can only contain letters and numbers",
        PET_NAME_LENGTH: "Pet name cannot be more than 50 characters long",
        PET_NAME_INVALID: "Pet name cannot match owner's name",
        PET_NAME_INVALID_PARTIAL: "Pet name cannot contain owner's name",
        PET_AGE_INVALID: "Pet age is invalid",
        PHONE: "Enter a valid phone number",
        PHONE_MOBILE: "Enter a valid mobile number",
        FIRST_NAME: {
            MIN: "First name must be at least 2 characters",
            MAX: "First name must be 50 characters or less"
        },
        LAST_NAME: {
            MIN: "Last name must be at least 2 characters",
            MAX: "Last name must be 50 characters or less"
        },
        EMAIL: {
            MAX: "Email must be 50 characters or less"
        },
        EMAIL_INVALID: "Enter a valid email address",
        ZIP_INVALID: "Enter a valid zip code",
        CITY_MAX: "City must be 50 characters or less",
        CITY_CHARS: "City must contain characters",
        FULL_NAME: {
            REQUIRED: "First and last names are required",
            MAX: "Full name must be 50 characters or less"
        },
        ADDRESS_MAX: "Address must be 50 characters or less",
        ADDRESS_CHARS: "Address must contain characters",
        POSTAL_CODE_INVALID: "Enter a valid postal code",
        CARD_CVV: "Invalid Security Code",
        CARD_EXPIRY: "Invalid exp date",
        DECLINE_GENERIC: "We couldn't process your payment",
        DECLINE_PREPAID: "Prepaid cards are not an eligible payment method",
        DECLINE_PREPAID_DESCRIPTION: "Please use a debit or credit card to complete your purchase.",
        PET_MIN_AGE: "Your pet needs to be at least 8 weeks old",
        REQUIRED: "Required",
        BREED_UNAVAILABLE_IN_STATE: "Re-select your breed to continue.",
        LOCATION_UNAVAILABLE: "We're sorry, Spot is not available in your area",
        MICROCHIP: "Microchip number must be between 9 - 15 digits",
        MICROCHIP_DUPLICATE: "Pets cannot have the same microchip number",
        MICROCHIP_INVALID_ASYNC: "Invalid microchip number",
        CONTACT_CONSENT: "Please agree to contact consent",
        LENGTH_MAX: "Too Long!",
        PASSWORD_MEMBER_CENTER: {
            HINT: "Must contain a number and a symbol",
            HINT_SUCCESS: "Contains a number and a symbol",
            MIN: "Password must be at least 6 characters",
            REQUIRED: "Password is required",
            NUMBER: "Password must contain one number",
            SPECIAL_CHAR: "Password must contain one special character"
        },
        CHECKING_ACCOUNT_NUMBER: {
            HINT: "Must be a checking account",
            MIN: "Must be at least 3 digits",
            MAX: "Must be at most 17 digits"
        },
        ROUTING_NUMBER: {
            LENGTH: "Must be a 9 digit US routing number"
        },
        DIGITS_ONLY: "Must contain only digits"
    };

    static SELLING_POINTS = {
        HEADER: "Why Choose Spot",
        SUBHEADER_BEST: "Don’t settle for anything less than the best.",
        SUBHEADER_ENJOY: " Enjoy more perks & better coverage with Spot.",
        MORE_PERKS: "More Perks",
        BETTER_COVERAGE: "Better Coverage",
        FEWER_RESTRICTIONS: "Fewer Restrictions"
    };

    static SPOT_ADDRESS = "100 Biscayne Blvd, Suite 400, Miami, FL 33132.";

    static PTZ_US = {
        META_TITLE: "Free Pet Insurance Quote | Pick Your Coverage",
        META_DESCRIPTION: "Spot is proud to offer pet parents a free pet insurance quote. Visit us to learn more about our different coverage options and what is best for you.",
        OG_TITLE: "Free Pet Insurance Quote | Pick Your Coverage",
        OG_DESCRIPTION: "Spot is proud to offer pet parents a free pet insurance quote. Visit us to learn more about our different coverage options and what is best for you.",
        OG_SITE_NAME: "Spot Pet Insurance",
        OG_IMAGE_ALT: "Spot Pet Insurance",
        TWITTER_TITLE: "Free Pet Insurance Quote | Pick Your Coverage",
        TWITTER_DESCRIPTION: "Spot is proud to offer pet parents a free pet insurance quote. Visit us to learn more about our different coverage options and what is best for you.",
        TWITTER_SITE: "@getspotcovered",
        SAMS_META_TITLE: "Sam’s Club Pet Insurance - Spot x Sam’s Pet Insurance",
        SAMS_META_DESCRIPTION: "Spot Pet Insurance partners with Sam’s Club to give Sam’s Club Members up to 15% off pet insurance. Get a free quote now!",
        COPYRIGHT_TEXT: "%year% United States Fire Insurance Company. Spot Pet Insurance Logo, Copyright %year%, Spot Pet Insurance. All Rights Reserved.",
        SELECT_COVERAGE_TEXT: "Select Coverage",
        BILLING_NAME: "Full Name",
        ADDRESS_LINE_1: "Address Line 1",
        ADDRESS_LINE_2: "Address Line 2",
        STATE: "State",
        PHONE_NUMBER: "18009051595",
        HOURS: "Mon – Fri | 8am – 8pm",
        // DISPLAY_NUMBER: "888-880-SPOT",
        PLAN_PDF_LINK_TEXT: "View Plan Details",
        SAMS_EXCLUSIVE: "Sam’s Club Member Exclusive",
        SAMS_SIGN_IN_HEADER: "Sign in to your Sam’s Club account",
        SAMS_SIGN_IN_CTA: "Click for Price",
        SAMS_SIGN_IN_DESCRIPTION: "Verify your Sam’s Club membership to get your discounts and gift card.",
        SAMS_CC_CALLOUT: "Use the Sam’s Club Mastercard at checkout!",
        CREATE_ACCOUNT: "Create an Account",
        PASSWORD_LABEL: "Password",
        CREATE_PASSWORD_CTA: "Create Account",
        ACCOUNT_CREATED_HEADER: "Account Created!",
        ACCOUNT_EXISTS_HEADER: "Account Already Exists",
        ACCOUNT_SETUP_INSTRUCTIONS: "Complete the account setup steps below for easier claim submission and faster reimbursement.",
        ACCOUNT_SETUP_COMPLETE_HEADER: "Account Setup Complete",
        ACCOUNT_SETUP_COMPLETE_HEADER_RETURNING: "Your Member Center account is already set up, nice work!",
        ADD_VET_CTA: "Add your vet",
        ADD_VET_INSTRUCTIONS: "Already have a vet? Add them now to expedite claim submission.",
        ADD_EPAY_CTA: "Set up direct deposit",
        ADD_EPAY_INSTRUCTIONS: "Get claim reimbursements faster (2-3 business days) with direct deposit.",
        VET_SEARCH_PLACEHOLDER: "Search for your vet",
        CHECKING_ACCOUNT_NUMBER_LABEL: "Account Number",
        ROUTING_NUMBER_LABEL: "Routing Number",
        NAME_ACCOUNT_LABEL: "Name on Account",
        MEMBER_CENTER_LINK_CTA: "Go to Member Center",
        MEMBER_CENTER_LOGIN_TEXT: "You can now log in to the Member Center to upload your pet medical records, set up direct deposit, submit claims and more.",
        CHECKING_ACCOUNT: "Checking account",
        GIFTCARD: {
            UNAVAILABLE: "Gift Card* Unavailable in Your State",
            ELIGIBLE: "You're Eligible for a",
            TOAST_TITLE: "Bonus!",
            BIGGEST_OFFER_TITLE: "Our Biggest Offer Ever!",
            BIGGEST_OFFER_SUFFIX: "is a few clicks away!",
            QUALIFY: "See if you qualify for a",
            PROBLEM: "We had a problem determining your eligibility",
            DISCLAIMER_TEXT: "Gift card is subject to eligibility, not available in all states.",
            DISCLAIMER_LINK_TEXT: "Eligibility terms."
        },
        PREVENTIVE_BASIC: "Gold",
        PREVENTIVE_ADVANCED: "Platinum",
        PET_HEALTH_HELPLINE: "24/7 Pet Health Helpline is Included in Spot plans too!",
        NOT_SAMS_MEMBER: "Not a Sam’s Club Member?",
        STANDARD_QUOTE_CTA: "Go to our standard quote form",

        COVERAGE_ACCIDENT: {
            VET_EXAM: { title: "Vet Exam Fees", description: "For allergies, stomach issues, etc.", accidentOnlydescription: "For car accidents, heatstroke, etc." },
            DIAGNOSTICS: { title: "Diagnostics", description: "Blood tests, x-rays, MRI’s & CT scans" },
            SURGERY: { title: "Surgery", description: "At licensed vets or hospitals" },
            MEDS: { title: "Prescription medications & food", description: "Including special diets and supplements" },
            TREATMENTS: { title: "Specialized Treatments", description: "Including acupuncture, microchipping, and more" },
            SWALLOWED_OBJECTS: { title: "Swallowed objects & toxic ingestions" },
            CUTS_WOUNDS: { title: "Cuts, bite wounds, insect stings" },
            BROKEN_BONES: { title: "Broken bones & other injuries" },
            EAR_CONDITIONS: { title: "Ear conditions" },
            RESPIRATORY_ILLNESSES: { title: "Respiratory illness" },
            NEURO_MUSCULAR_DISORDERS: { title: "Neuro-Muscular disorders" },
            BEHAVIORAL_CONDITIONS: { title: "Behavioral conditions" },
            EHRLICHIA: { title: "Ehrlichia (tick-borne disease)" },
            BRAIN_TUMORS: { title: "Brain tumors" },
            HEART_MURMURS: { title: "Heart murmurs" },
            HEART_CONDITIONS: { title: "Heart conditions" },
            RECTAL_CONDITIONS: { title: "Rectal conditions" },
            EYE_CONDITIONS: { title: "Eye conditions" },
            BLOOD_DISORDERS: { title: "Blood disorders" },
            PARASITES: { title: "Parasites" },
            INFECTIOUS_DISEASES: { title: "Infectious diseases" },
            SKIN_INFECTIONS: { title: "Skin infections" },
            STOMACH_CONDITIONS: { title: "Stomach conditions" },
            GASTROINTESTINAL_STOMACH_CONDITIONS: { title: "Gastrointestinal conditions" },
            KIDNEY_DISEASES: { title: "Kidney diseases" },
            ALLERGIC_DERMATITIS: { title: "Allergic dermatitis" },
            HEART_FAILURE: { title: "Heart failure" },
            URINARY_TRACT_INFECTIONS: { title: "Urinary tract infections" },
            LEG_INJURIES: { title: "Leg injuries" },
            BACK_NECK_INJURIES: { title: "Back/neck injuries" },
            RENAL_FAILURE: { title: "Renal failure" }
        },

        COVERAGE_ILLNESS: {
            BREED: {
                title: "Breed-specific, congenital & genetic conditions",
                covers: [
                    {
                        title: "Allergies",
                        affects: [
                            { breedName: "Golden Retriever", rate: 1.62, displayAs: "percentage" as const },
                            { breedName: "French Bulldog", rate: 1.69, displayAs: "percentage" as const, avgClaimAmount: 1912, claimFrequency: 3617 }
                        ]
                    },
                    { title: "Hip dysplasia" },
                    { title: "Cataracts" },
                    { title: "Degenerative myelopathy" },
                    { title: "Intervertebral disc disease (IVDD)" }
                ]
            },
            DENTAL: {
                title: "Dental Illnesses",
                covers: [
                    { title: "Tooth Trauma (Broken, chipped & fractured teeth)" },
                    { title: "Extractions" },
                    { title: "Gingivitis" },
                    { title: "Periodontal disease", affects: [{ breedName: "Small Mix", rate: 1.77, avgClaimAmount: 1469, claimFrequency: 1258 }] }
                ]
            },
            CANCER: {
                title: "Cancer",
                covers: [
                    { title: "Hermangiosarcoma" },
                    { title: "Lipoma" },
                    { title: "Lymphoma", affects: [{ breedName: "Large Mix", rate: 2.75, displayAs: "percentage" as const }] },
                    { title: "Mast cell tumor" },
                    { title: "Melanoma" },
                    { title: "Osteosarcoma" }
                ]
            },
            CHRONIC: {
                title: "Chronic conditions",
                covers: [
                    { title: "Allergies" },
                    { title: "Arthritis", affects: [{ breedName: "Large Mix", rate: 2, avgClaimAmount: 1785, claimFrequency: 2538 }] },
                    { title: "Diabetes", affects: [{ breedName: "Small Mix", rate: 1.69 }] },
                    { title: "Kidney disease" },
                    { title: "Hyperthyroidism" },
                    { title: "Inflammatory bowel disease" },
                    { title: "Skin irritation", affects: [{ breedName: "German Shepherd", rate: 2.6 }] }
                ]
            },
            ORTHOPEDIC: {
                title: "Orthopedic conditions",
                covers: [
                    { title: "Broken bones" },
                    { title: "Cruciate ligament tear", affects: [{ breedName: "Large Mix", rate: 2.5 }] },
                    { title: "Disc disease" },
                    { title: "Hip dysplasia" },
                    { title: "Luxating patella" },
                    { title: "Osteosarcoma" }
                ]
            }
        },

        BREED_DISPLAY_MAPPINGS: [
            { breedLabel: "Golden Retriever", pluralLabel: "Golden Retrievers" },
            { breedLabel: "Labrador Retriever", pluralLabel: "Labrador Retrievers" },
            { breedLabel: "French Bulldog", pluralLabel: "French Bulldogs" },
            { breedLabel: "Small Mix", pluralLabel: "Small Mixed Breeds" },
            { breedLabel: "Medium Mix", pluralLabel: "Medium Mixed Breeds" },
            { breedLabel: "Large Mix", pluralLabel: "Large Mixed Breeds" },
            { breedLabel: "American Shorthair", pluralLabel: "American Shorthair Cats" },
            { breedLabel: "Domestic Shorthair", pluralLabel: "Domestic Shorthair Cats" },
            { breedLabel: "Domestic Mediumhair", pluralLabel: "Domestic Mediumhair Cats" },
            { breedLabel: "Domestic Longhair", pluralLabel: "Domestic Longhair Cats" },
            { breedLabel: "Mixed Cat", pluralLabel: "Mixed Cats" }
        ],

        EXCLUSIONS: {
            PRE_EXISTING_CONDITIONS:
                "Most pet insurance providers don’t cover pre-existing conditions, which are conditions that occur before coverage begins or during a waiting period. However, we will no longer consider a condition to be pre-existing if it has been cured and free of symptoms and treatment for 180 days, with the exception of knee and ligament conditions. If a knee or ligament condition occurs before the coverage effective date or during a waiting period, any future ones won’t be covered."
        },

        BENEFITS_PREVENTIVE: {
            SPAY_OR_DENTAL: "Spay/Neuter or Dental Cleaning",
            DENTAL_OR_SPAY: "Dental Cleaning or Spay/Neuter"
        },

        PUPPY_KITTEN_AGE: "8 weeks to 11 months",

        // ENG-18695 — multi-pet discount visibility experiment. Client-side experiment copy, so it lives
        // here and NOT in the Features API partner registry (that file is for partner-targeted copy).
        // The ¹ footnote is Strings.MULTIPET_GENERIC_DISCLAIMER, rendered by the pets-step footer.
        MULTIPET_OFFER: {
            bold: "OFFER APPLIED:",
            offer: "10% off multi-pet discount¹"
        },

        STATS: {
            EMERGENCY_VET_TREATMENT: "1 in 3 pets need emergency veterinary treatment every year",
            VET_BILL: "Every 6 seconds, a pet owner is faced with a vet bill of $1,000 or more"
        },
        /** Shared by control's breed carousel and the ENG-16104 variant's reviews slide, so the two match. */
        REVIEWS_CAPTION: "Reviews posted within last 12 months",
        // test start ENG-16104 "What to Know" section
        WHAT_TO_KNOW: {
            HEADING: "What you should know",
            TABS: {
                // NOTE(ENG-16104): the design file disagrees with itself on this label — the 360 slide-1
                // frame says "What parents are saying", every other frame says "What are parents sayin".
                // Using the majority reading; open question 1 in the brief.
                REVIEWS: "What are parents sayin",
                WHY: "Why protect your pet"
            },
            REVIEWS: {
                RATING_PREFIX: "stars on",
                RATING_SUFFIX: "reviews",
                RATING_BASED_ON: "based on"
            },
            WHY: {
                CLOSING: "Unexpected vet bills can add up fast — even in young, healthy pets.",
                DIAGRAM_LABEL:
                    "Illustration of a dog labeled with commonly claimed conditions: hip dysplasia, cataracts, dental illnesses, cancer, swallowed objects and broken bones",
                STAT_VET_BILL: {
                    HEADLINE: "Every 6 Seconds",
                    SUPPORT: "a pet owner faces a vet bill of",
                    EMPHASIS: "$1,000 or more.",
                    FOOTNOTE: 1
                },
                STAT_EMERGENCY: {
                    HEADLINE: "1 in 3 Pets",
                    SUPPORT: "needs emergency vet care",
                    EMPHASIS: "each year.",
                    FOOTNOTE: 2
                }
            }
        },
        FAQS_GENERAL: [
            {
                question: "What is the Best Age to Insure My Pet?",
                answer: "Plans provided by Spot don’t have an upper age limit for new enrollments or coverage. We advise all pet parents to insure their pets at a young age. You can enroll your pet in a plan from Spot as soon as they’re 8 weeks old. Don’t let your pet develop a “pre-existing condition” before they’re insured."
            },
            {
                question: "What is a Pre-Existing Condition?",
                answer: "A pre-existing condition is any injury or illness which occurs or shows symptoms before coverage starts or during a waiting period. A condition is considered pre-existing whether or not it’s been officially diagnosed or treated; all that matters is when it occurred or symptoms first displayed. Plans provided by Spot do not cover pre-existing conditions. However, with pet insurance plans provided by Spot, this doesn’t mean the condition can’t be covered in the future if the condition is curable. An injury or illness that is curable, cured, and free of treatment and symptoms for 180 days will no longer be regarded as pre-existing, with the exception of knee and ligament conditions. If a knee or ligament condition occurs before the coverage effective date or during a waiting period, any future ones won’t be covered."
            },
            {
                question: "Does Spot Offer a Plan for Preventive Care Coverage?",
                answer: "Yes, you can add preventive care coverage to your policy for a little more cost. When you add preventive care coverage, you’ll be reimbursed up to the scheduled amount for things that help protect your pet from getting sick. You don’t have to satisfy your deductible before you can be reimbursed for preventive care coverage benefits, and there is no waiting period for them either."
            },
            {
                question: "How are My Pet Insurance Premiums Calculated?",
                answer: "In general, your premium is based on your pet’s age, your pet’s breed, and where your pet lives. Some other factors that impact your premium are: the plan you choose, your deductible, your reimbursement percentage, if you have included preventative care coverage or not, and the discounts for which you are eligible."
            }
        ],
        // test end
        // test start ENG-18612 Annual Billing Opt-In Gravitas
        ANNUAL_BILLING: {
            MONTHLY_LABEL: "Monthly",
            ANNUAL_LABEL: "Annual",
            /** Split so "Save $X/yr" can be bolded while the tail stays medium-weight, per Figma. */
            SAVINGS_PROMPT_EMPHASIS: "Save %amount%/yr",
            SAVINGS_PROMPT_TAIL: " with annual billing.",
            SAVINGS_CONFIRMED: "You’re saving %amount% per year!"
        },
        // test end
        PERKS_DISCLAIMER: "Eligibility is open to all. Based on total combined Spot Perks discounts applied to avg. vendor cart value.",
        // ENG-16914: footnote ² on the personalized pets-step hero's "Extensive coverage" blurb.
        ACCIDENT_ILLNESS_DISCLAIMER: "Only covered with an accident and illness plan. Pre-existing conditions are not covered.",
        APP_STORE_TITLE: "Submit claims in minutes on your phone",
        APP_STORE_DESCRIPTION: "Submit claims, check statuses, and access 24/7 vet support in the app.",

        PRIVACY_DISCLAIMERS: {
            OUR_PROMISE: "We protect your personal information and never sell your data.",
            PRIVACY_PROTECTION: "We protect your privacy and personal data."
        },
        MOBILE_EXPLAINERS: {
            PROTECT_INFO: "For your saved quote—we protect your info.",
            TEXT_MESSAGE: "We’ll text your personalized quote here."
        },
        EMAIL_EXPLAINERS: {
            PROTECT_YOUR_INFO: "For your saved quote—we protect your info.",
            EMAIL_DELIVERY: "We’ll email your personalized quote here."
        },
        NUMBERED_TEST: {
            PREVENTIVE_CARE_HEADER: "Add preventive care coverage",
            PREVENTIVE_CARE_SUBHEADER: "Get fixed reimbursements for wellness care every year."
        },
        COVERAGE_CTA_SOCIAL_PROOF: {
            HEADLINES: {
                CUMULATIVE: "1,000,000+ pets protected to date",
                RECENCY: "3,000+ pets enrolled this week"
            }
        },
        CUSTOMIZE_PLAN: "Customize your plan"
    };
    static PTZ_CA = {
        META_TITLE: "Get a Free Pet Insurance Quote in Canada | Spot Pet Insurance",
        META_DESCRIPTION: "Get a free pet insurance quote in minutes. Compare customizable plans for dogs and cats in Canada, with up to 90% reimbursement on covered vet bills.",
        ZIP_CODE_HINT: "Where your pet lives",
        BREED_LOCKED_TITLE: "Enter your postal code first",
        BREED_LOCKED_DESCRIPTION: "Breed lists vary by region, we'll show the list for your area.",
        BREED_LOCKED_CTA: "Go to postal code",
        BREED_UNAVAILABLE_AREA_TITLE: "No breeds available for your area",
        BREED_UNAVAILABLE_AREA_DESCRIPTION: "We couldn't find a breed list for your postal code.",
        RELOAD_PAYMENT_FORM: "Reload payment form",
        // OpenGraph metadata
        OG_TITLE: "Get a Free Pet Insurance Quote in Canada | Spot Pet Insurance",
        OG_DESCRIPTION: "Get a free pet insurance quote in minutes. Customize plan options for dogs and cats in Canada and see pricing instantly.",
        OG_SITE_NAME: "Spot Pet Insurance Canada",
        OG_IMAGE_ALT: "Spot Pet Insurance Canada",

        // Twitter metadata
        TWITTER_TITLE: "Get a Free Pet Insurance Quote in Canada | Spot Pet Insurance",
        TWITTER_DESCRIPTION: "Get a free pet insurance quote in minutes. Customize plan options for dogs and cats in Canada and see pricing instantly.",
        TWITTER_SITE: "@getspotcovered",

        COPYRIGHT_TEXT: "%year% All Rights Reserved. Spot Pet Insurance Logo, Copyright %year%, Spot Pet Insurance.",
        SELECT_COVERAGE_TEXT: "Select Coverage",
        PHONE_NUMBER: "18885017768",
        HOURS: `Mon – Fri | 8am – 8pm EST \n Sat | 9am – 5pm EST`,
        POSTAL_CODE: "Postal Code *",
        POSTAL_CODE_PLACEHOLDER: "V5R 4B3",
        CHECKOUT_CONTINUE: "Continue to payment",
        PAPERLESS: {
            LABEL: "Go paperless",
            CHECKED_HINT: "Get your policy and communications online.",
            UNCHECKED_HINT: "Your policy documents and other communications will be mailed to the address below."
        },
        MAILING_ADDRESS: {
            HEADER: "Mailing Address",
            ADDRESS_LINE_1: "Address Line 1",
            ADDRESS_LINE_2: "Address Line 2",
            CITY: "City",
            PROVINCE: "Province",
            POSTAL_CODE: "Postal code",
            STATE_MISMATCH: "Enter the province that matches your postal code"
        },
        PLAN_PDF_LINK_TEXT: "View Plan Details",
        PREVENTIVE_BASIC: "Routine",
        PREVENTIVE_ADVANCED: "Advanced",
        UP_TO_MULTIPET_DISCOUNT: "Up to 10% off on additional pets",
        ADD_PET_GET_DISCOUNT: "Add a Pet, Get Up To 10% Off",
        PREVENTIVE_CARE_SUBHEADER: "Get fixed reimbursements for wellness care every year.",
        PREVENTIVE_CARE_HEADER: "Add Preventive Care Coverage",

        EXCLUSIONS: {
            PRE_EXISTING_CONDITIONS:
                "A pet's illness or accident is considered pre-existing if the condition was onset before the policy coverage effective dates, or during a waiting period. This means if the first signs or symptoms of a condition showed up before coverage started, it would be considered pre-existing to coverage, even if it was not formally diagnosed."
        },

        WAITING_PERIODS: {
            intro: "The following Waiting Periods apply immediately after enrollment:",
            items: [
                "Accident coverage and Optional Wellness add-on coverage both begin at 12:01 am of the policy start date (the day after enrollment).",
                "Illness coverage begins at 12:01 am on day 15 of the policy."
            ]
        }
    };
    static DATA_DOG = {
        POLICY_STEP_CTA: "Policy Step CTA",
        COVERAGE_STEP_CTA: "Coverage Step CTA",
        // ENG-17301 — kept distinct from COVERAGE_STEP_CTA so each test variant's checkout clicks
        // are measurable against the footer CTA's.
        COVERAGE_STICKY_CHECKOUT_CTA: "Coverage Step Sticky Menu Checkout CTA",
        COVERAGE_FLOATING_CHECKOUT_CTA: "Coverage Step Floating Checkout CTA",
        BILLING_STEP_CTA: "Billing Step CTA"
    };

    static PREVENTIVE_ITEMS = {
        DHLPP: {
            DOG: "DHLPP Vaccine/Titer",
            CAT: "FVRCP Vaccine/Titer",
            MIXED: "DHLPP or FVRCP Vaccine/Titer"
        },
        TESTS: {
            DOG: "Heartworm Test",
            CAT: "FELV Test",
            MIXED: "Heartworm or FELV Test"
        },
        RABIES: {
            DOG: "Rabies and/or Lyme Vaccine/Titer",
            CAT: "FIP Vaccine/Titer",
            MIXED: "Rabies/Lyme or FIP Vaccine/Titer"
        },
        BORDETELLA: {
            DOG: "Bordetella Vaccine/Titer",
            CAT: "FELV Vaccine/Titer",
            MIXED: "Bordetella or FELV Vaccine/Titer"
        }
    };

    static PREVENTIVE_CATEGORIES = {
        PROCEDURES: "Procedures",
        TREATMENTS: "Routine Treatments",
        TESTS: "Tests & Exams",
        VACCINES: "Vaccines & Titers",
        DOCUMENTATION: "Documentation"
    };

    // test start ENG-18988 prevCareCompare — copy for the gold/platinum side-by-side compare table.
    // The categorized arm collapses PREVENTIVE_CATEGORIES' five sections into three, with its own
    // titles, so these deliberately do not reuse the constants above.
    static PREVENTIVE_COMPARE = {
        BENEFITS_LABEL: "INCLUDED BENEFITS:",
        BENEFITS_LABEL_SHORT: "INCL. BENEFITS:",
        PLATINUM_SUBCOPY: "More cash-back and benefits",
        PLATINUM_SUBCOPY_SHORT: "More cash-back & benefits",
        PLATINUM_SUBCOPY_SHORTEST: "More benefits",
        GOLD_DENTAL_NOTE: "Only Dental Cleaning*",
        NOT_INCLUDED: "—",
        TABLE_LABEL: "Compare preventive care benefits",
        ADD: "Add",
        ADDED: "Added",
        REMOVE: "Remove",
        POPULAR_FOR: "Popular for",
        GROUPS: {
            TREATMENTS: "Treatments",
            TESTS: "Tests & Exams",
            TESTS_SHORT: "Tests",
            VACCINES: "Vaccines"
        }
    };

    static PREVENTIVE_COMPARE_GOLD_SUBCOPY = (totalBenefits: number) => `Up to $${totalBenefits}/yr in benefits`;
    // test end

    static UPDATE_BILLING = {
        VERIFY: {
            HEADING: "Verify Account",
            INFO_SUBHEADER: "Please enter your Account ID below and click verify to proceed.",
            ACCOUNT_ID_LABEL: "Account ID",
            ACCOUNT_ID_PLACEHOLDER: "SP1234567",
            VERIFY_BUTTON_LABEL: "Verify"
        },
        UPDATE: {
            HEADER: "Update Payment Method",
            SUBHEADER: "Enter your new payment information below. Your account will be updated with this new payment method.",
            UPDATE_STEP_DATADOG_CTA_NAME: "Update Step CTA",
            UPDATE_STEP_CTA_LABEL: "Update Payment"
        },
        ERRORS: {
            LOAD_FAILED_HEADER: "Unable to load payment details",
            LOAD_FAILED_SUBHEADER: "We couldn't retrieve your payment details. Please verify your Account ID below and try again.",
            VERIFY_FAILED: "Failed to verify account ID. Please try again.",
            ACCOUNT_ID_INVALID: "Invalid Account ID format.",
            INITIAL_ACCOUNT_ID_INVALID: "Initial Account ID is invalid."
        }
    };

    static POLICY_CALLOUT_CONTENT = {
        tagline: "America's Most Loved Pet Insurance.",
        simplifiTagline: "America's Most Loved Pet Insurance",
        titles: {
            whySpot: "Worried about unexpected vet bills? We've got you covered.",
            accolades: "Awarded by pet experts. Trusted by pet parents.",
            oneStep: "You're one step away from your Spot quote.",
            simplifiLifestyle: "More adventures. Less “what if.”",
            simplifiReassurance: "You take care of them. We help take care of the bills."
        },
        subheadings: {
            simplifiLifestyle: "Coverage that keeps up with the dog who's always two steps ahead — dog parks, road trips, and the occasional “how did they swallow that?”",
            simplifiReassurance: {
                prefix: "Coverage for eligible ",
                bold: "vet visits, surgeries, prescriptions",
                suffix: ", and the unexpected — built around your pet's life."
            }
        },
        personalizedQuote: "Get a personalized quote in 30 seconds or less!",

        // ENG-16914: species-personalized pets-step hero. Keyed by the species derived from the
        // referring page (see PetPersonalizationUtils); control uses the generic copy above.
        personalization: {
            dog: {
                tagline: "America's Most Loved Dog Insurance.",
                title: "Worried about unexpected vet bills? We've got your pup covered.",
                blurbs: [
                    { heading: "Chronic & hereditary coverage", description: "Hip dysplasia, allergies, cancer, and more" },
                    { heading: "Extensive coverage", description: "Behavioral modification, prescription foods, and more", reference: "2" },
                    { heading: "$2,500", description: "in perks from your favorite dog brands", reference: "3" }
                ],
                testimonial: {
                    heading: "Trusted by Dog parents like you.",
                    name: "Katie",
                    condition: "Hip Dysplasia",
                    quote: '"Spot Pet Insurance has been wonderful...they promptly approved the estimate from the orthopedic vet for surgery."'
                }
            },
            cat: {
                tagline: "America's Most Loved Cat Insurance.",
                title: "Worried about unexpected vet bills? We've got your kitty covered.",
                blurbs: [
                    { heading: "Chronic & hereditary coverage", description: "Kidney disease, hyperthyroidism, cancer and more" },
                    { heading: "Extensive coverage", description: "Dental illnesses, prescription foods, and more", reference: "2" },
                    { heading: "$2,500", description: "in perks from your favorite cat brands", reference: "3" }
                ],
                testimonial: {
                    heading: "Trusted by Cat parents like you.",
                    name: "Tobias",
                    condition: "Giardia",
                    quote: '"We are so glad that we got Spot. The insurance plan worked like a charm. Claims were easy to file and were processed quickly."'
                }
            }
        }
    };

    // In Strings.constants.ts, inside the Strings class

    static SCHEMA_STRINGS = {
        ORGANIZATION_NAME: "Spot Pet Insurance",
        PRODUCT_NAME_SAMS: "Spot X Sam’s Club Pet Insurance",
        PRODUCT_NAME_US_QUOTE: "Spot Pet Insurance Quote Form",
        SERVICE_NAME_PET_INSURANCE: "Pet Insurance",
        SERVICE_TYPE_DOG_CAT: "Dog & Cat Insurance",

        // US Quote
        WEBPAGE_NAME_US_QUOTE: "Get a Free Pet Insurance Quote - Spot Pet Insurance",
        WEBPAGE_DESC_US_QUOTE: "Get a free, custom pet insurance quote from Spot in seconds. See how much you can save on eligible vet bills for your dog or cat.",

        // Canada Quote
        WEBPAGE_NAME_CA_QUOTE: "Free Pet Insurance Quote in Canada",
        WEBPAGE_DESC_CA_QUOTE: "Get a free pet insurance quote for your dog or cat in Canada. Customize plan options and see pricing in minutes.",

        // Sam’s Club
        WEBPAGE_NAME_SAMS: "Sam’s Club - Spot Pet Insurance – Exclusive Member Discount",
        WEBPAGE_DESC_SAMS: "Sam’s Club Members enjoy up to 15% discount on pet insurance through Spot Pet Insurance plus Pet RX savings at Sam’s Club Pharmacy",
        OFFER_NAME_SAMS: "Sam’s Club Pet Insurance Member Discount",
        OFFER_DESC_SAMS: "Up to 15% member discount on Spot Pet Insurance plus Pet RX savings for Sam’s Club members."
    };

    static PREVENTIVE_CARE_EXAMPLE = {
        HEADING: (dateText: "today" | "tomorrow") => `Make your first preventive care claim ${dateText}!`,
        SUBHEADING: "From routine check-ups to dental cleanings, your plan's ready to help.",
        BADGE: "Claim example",
        EXAMPLE_TITLE: "Dental Cleaning",
        ROW_VET_CHARGES: "Your vet charges",
        ROW_SPOT_PAYS: "Spot pays you back",
        ROW_YOUR_COST: "Your cost after reimbursement:",
        FOOTER: "This is just one example- your plan also helps cover vaccines, check-ups, and more!",
        DISCLAIMER:
            "Claim reimbursement is based on actual policyholder claims 2022. Reimbursement amounts may vary depending on the specific terms of the policy, including coverage selected, annual deductible, reimbursement rate, and annual limit. Individual experiences and outcomes may vary."
    };

    static AI_INSIGHTS = {
        FOOTER_LABEL: "Spot AI Insights",
        SEE_MORE: "See More",
        CLOSE: "Close",
        VET_BILL_TEXT: "in annual vet bills",
        MORE_PRONE_TO: "more prone to",
        AVG_VET_BILL: "avg vet bill:",
        WITH_SPOT: "w/Spot"
    };

    static AGGREGATOR_COMPARISON = {
        // Feature text variants
        FIRST_FEATURE_NATURAL_INTELLIGENCE: "Access to partner perks",
        FIRST_FEATURE_CAPSOOL: "$2,500+ in partner perks",

        // Table links & badges
        MORE_LINK_ARIA_LABEL: "View complete coverage comparison",
        WHY_SPOT_BADGE: "Why choose Spot?",

        // Features (main table)
        FEATURE_DENTAL_ILLNESSES: "Dental illnesses",
        FEATURE_PRESCRIPTION_FOOD: "Prescription food",
        FEATURE_TELEHEALTH: "24/7 pet telehealth line",
        FEATURE_BEHAVIORAL_MODIFICATION: "Behavioral modification",

        // Modal content
        MODAL_HEADING: "See how our competition stacks up",
        MODAL_SUBHEADING: "Swipe to see more",
        MODAL_FOOTNOTE_PERKS: "$2,500+ in perks & discounts",

        // Category headers
        CATEGORY_MORE_PERKS: "MORE PERKS",
        CATEGORY_BETTER_COVERAGE: "BETTER COVERAGE",
        CATEGORY_MORE_FLEXIBILITY: "MORE FLEXIBILITY",

        // Provider names
        PROVIDER_HEALTHY_PAWS: "Healthy Paws",
        PROVIDER_LEMONADE: "Lemonade",
        PROVIDER_PETS_BEST: "Pets Best",
        PROVIDER_PROGRESSIVE: "Progressive",
        PROVIDER_METLIFE: "Metlife",
        PROVIDER_FETCH: "Fetch",
        PROVIDER_FIGO: "Figo",
        PROVIDER_EMBRACE: "Embrace",
        PROVIDER_ASPCA: "ASPCA",
        PROVIDER_PUMPKIN: "Pumpkin",

        // Modal features
        FEATURE_MICROCHIPPING: "Microchipping",
        FEATURE_ALTERNATIVE_THERAPIES: "Alternative therapies",
        FEATURE_NO_LIFETIME_CAPS: "No lifetime caps",

        // Disclaimers
        DISCLAIMER_COMPARISON_INFO: "Comparison information is provided using publicly available information",
        DISCLAIMER_NO_PURCHASE: "No purchase necessary. Based on total combined Spot Perks discounts",

        // Accolades
        ACCOLADE_FORBES: "Forbes",
        ACCOLADE_TRUSTPILOT: "Trustpilot",
        ACCOLADE_MARKET_WATCH: "Market Watch",

        TABLE_LEGEND_COVERED: "Covered"
    };

    static MULTI_PLAN_GUIDE = {
        NUDGE_TITLE: "Need help choosing a plan?",
        NUDGE_BODY: "See what pet parents typically choose.",
        NUDGE_DISMISS: "No thanks",
        NUDGE_CTA: "Help me choose",
        MODAL_TITLE_SINGLE: "Choose your pet plan",
        MODAL_TITLE_MULTI: "Choose your pet's plans",
        MODAL_SUBTITLE: "Most pet parents choose one of these plans:",
        COVERAGE_LABEL: "Coverage",
        ACCIDENT_ILLNESS: "Accident & Illness",
        REIMBURSEMENT: "Reimbursement",
        ANNUAL_LIMIT: "Annual Limit",
        ANNUAL_DEDUCTIBLE: "Annual Deductible",
        SELECT: "Select",
        SELECTED: "Selected",
        EDIT: "Edit",
        DONE: "Done",
        SUCCESS_TOAST_SINGLE: "Plan applied successfully!",
        SUCCESS_TOAST_MULTIPLE: "Plans applied successfully!",
        PER_MONTH: "/mo",
        PETS_GROUP_HEADER: (count: number) => `${count} pets`
    };
}
